import { Collapsible } from './utilities.gts';
import { on } from '@ember/modifier';
import { or } from '../utilities.ts';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import type { TOC } from '@ember/component/template-only';
import type Owner from '@ember/owner';
import { PhCaretDown } from 'ember-phosphor-icons';

// NOTE: Tailwind 4 CSS-first migration
// We removed dynamically injected template string class groups (SectionStyle / SectionDarkStyle)
// and replaced them with a static, analyzable class list plus data attribute variants.
// This avoids complex :has chains that were intermittently dropped by the Tailwind 4 parser.

interface VStackSignature {
  Args: {
    /**
     * When true the direct children will be separated by a horizontal divider
     *
     * @optional
     * @type {boolean}
     */
    collapsed?: boolean;
  };
  Element: HTMLDivElement;
  Blocks: { default: [] };
}
/**
 * Vertical Stack
 *
 * A view that arranges its subviews in a vertical line.
 */
export const VStack: TOC<VStackSignature> = <template>
  <div data-stack-collapsed={{if @collapsed "true" "false"}} data-vstack ...attributes>
    {{yield}}
  </div>
</template>;

interface HStackSignature {
  Args: {
    /**
     * When true the direct children will be separated by a vertical divider
     *
     * @optional
     * @type {boolean}
     */
    collapsed?: boolean;
  };
  Element: HTMLDivElement;
  Blocks: { default: [] };
}
/** Horizontal Stack
 *
 * A view that arranges its subviews in a horizontal line.
 */
export const HStack: TOC<HStackSignature> = <template>
  <div data-stack-collapsed={{if @collapsed "true" "false"}} data-hstack ...attributes>
    {{yield}}
  </div>
</template>;

// TODO: return to this. figure out the rule that would make a header adjust its color based on its parent
// or just make another component called sub header.
// const headerCheck = modifier(
//   (element: HTMLElement) => {
//     let parent = element.parentElement;
//     let grandparent = parent?.parentElement;

//     // Condition:
//     // 1. The parent has 'data-vstack'
//     // 2. The grandparent also has 'data-vstack'
//     // 3. This element is the parent's first child
//     if (
//       parent?.hasAttribute('data-vstack') &&
//       grandparent?.hasAttribute('data-vstack') &&
//       parent.firstElementChild === element
//     ) {
//       element.classList.add('text-gray-600');
//     } else {
//       element.classList.add('text-midnight', 'text-xl');
//     }
//   }
// );

interface HeaderSignature {
  Args: Record<string, never>;
  Element: HTMLElement;
  Blocks: { default: [] };
}
export const Header: TOC<HeaderSignature> = <template>
  <header class="tio-text-brand" data-header ...attributes>
    {{yield}}
  </header>
</template>;

interface SectionSignature {
  Args: {
    headerClass?: string;
    collapsed?: boolean;
  };
  Element: HTMLElement;
  Blocks: { default: []; header: []; subHeader: []; actions: []; body: [] };
}
/** Section
 *
 * A container view that you can use to add hierarchy within certain views.
 *
 * Use Sections in views like HStack and VStack to organize content
 * into separate sections. Each section has custom content that you
 * provide on a per-instance basis. You can also provide headers section.
 */
export class Section extends Component<SectionSignature> {
  @tracked isOpen = true;

  constructor(owner: Owner, args: SectionSignature['Args']) {
    super(owner, args);
    if (args.collapsed) {
      this.isOpen = !args.collapsed;
    }
  }

  get isCollapsible() {
    return this.args.collapsed !== undefined;
  }

  toggle = () => {
    this.isOpen = !this.isOpen;
  };

  <template>
    <section
      data-section
      data-open={{if this.isOpen "true" "false"}}
      data-has-body={{if (has-block "body") "true" "false"}}
      ...attributes
    >
      {{#if
        (or this.isCollapsible (has-block "header") (has-block "subHeader") (has-block "actions"))
      }}
        <div data-section-header-row>
          {{#if this.isCollapsible}}
            <button
              type="button"
              class="hover:text-ocean-800 h-6"
              {{on "click" this.toggle}}
              data-section-caret-trigger
            >
              <PhCaretDown
                class="w-4 h-4 text-ocean-600 transition-transform duration-200 ease-out will-change-transform
                  {{if this.isOpen 'rotate-0' '-rotate-90'}}"
                data-section-caret
              />
            </button>
          {{/if}}
          <div data-section-header-inner>
            <div data-section-header-text>
              {{#if (has-block "header")}}
                <h1
                  class="font-semibold text-black dark:text-white {{@headerClass}}"
                  data-section-header
                >
                  {{yield to="header"}}
                </h1>
              {{/if}}
              {{#if (has-block "subHeader")}}
                <h2 class="text-sm" data-section-sub-header>
                  {{yield to="subHeader"}}
                </h2>
              {{/if}}
            </div>
            {{#if (has-block "actions")}}
              <div class="flex items-end gap-2" data-section-actions>
                {{yield to="actions"}}
              </div>
            {{/if}}
          </div>
        </div>
        {{#if this.isOpen}}
          <div class="h-0 border-b border-gray-200 dark:border-b-gray-700/70 mx-0"></div>
        {{/if}}
      {{/if}}

      {{#if (has-block "body")}}
        <Collapsible @isOpen={{this.isOpen}}>
          {{! Need to do more testing around these p-0 overrides. So far immediate v/hstack indicates
              pure layout and the body is providing the padding. recommendations.gts is why I added this. }}
          <div data-section-body-wrapper data-section-body>
            {{yield to="body"}}
          </div>
        </Collapsible>
      {{/if}}
      {{yield}}
    </section>
  </template>
}
